import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import _ from "lodash";
import { setShowApprovalLogs, setShowParentTaskDescription, setShowTaskDetailPanel } from "../../../actions/sidebarContent";
import { getFocusedSection, setExpanded, setShowEmailDescPopup, setShowTriggerTaskPopup, setTempTaskDesc } from "../../../actions/taskSidebar";
import { number } from "../../../config";
import { useEditorContext } from "../../../editor/editor.Context";
import { FormProvider } from "../../Forms";
import { FLOW_ELEMENTS_SECTION_ID, getOrderedNavbarOptionsForTaskSidebar } from "../../Tasks/tasks.constants";
import { handleDescSection } from "../sidebar.helper";
import Approvals from "./Approvals/Approvals";
import TaskComments from "./Comments/TaskComments";
import CustomPanelBarItemContent from "./CustomPanelBarItemContent";
import DescriptionPopup from "./Description/DescriptionPopup";
import ParentTaskDetailPanel from "./ParentTaskDetailPanel/ParentTaskDetailPanel";
import EmailDescription from "./TaskEmails/EmailDescription";
import TaskEmail from "./TaskEmails/TaskEmail";
import TaskForm from "./TaskForms/TaskForm";
import "./TaskSidebarContent.scss";
import TriggeredTasksPopup from "./TriggeredTasksPopup/TriggeredTasksPopup";
import ViewControlPanel from "./WorkflowElementsPanel/ViewControlPanel";
import WorkflowElementsPanel from "./WorkflowElementsPanel/WorkflowElementsPanel";
import { ActivityLog, ApprovalDescription, Attachments, Comments, LinkedSubTasks, TaskDescription, TaskDetail } from "./index";

/**
 * TaskSidebarContent component
 * @props {}
 */
const TaskSidebarContent = ({ provider, setProvider, flowElementsSectionRef }) => {
  // const [provider, setProvider] = React.useState(null);
  const [offset, setOffset] = useState(0);
  const [currentEmailData, setCurrentEmailData] = useState(true);
  const dispatch = useDispatch();
  const {
    expanded,
    task,
    currentFocus,
    showDescPopup,
    showTriggerTaskPopup,
    isNewTask,
    newTaskData,
    showTransferOwnershipPopup,
    showEmailDescPopup,
    showFormPopup,
    showDescription: showDescriptionFromTaskSidebar,
  } = useSelector((state) => state.taskSidebar);
  const { showSimplifiedDetail } = useSelector((state) => state.taskSidebar);
  const { defaultDetails } = useSelector((state) => state.tasks);
  // tempDesc is used for holding the current desc value.
  const [tempDesc, setTempDesc] = useState(null);
  const { showTaskDetailPanel, showApprovalLogs, showDescription } = useSelector((state) => state.sidebarContent);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const expandValue = queryParams.get("expand");

  /**
   * Handles expansion of comment section based on the URL query parameter `expandValue`.
   * @author Bhavana
   */
  useEffect(() => {
    if (parseInt(expandValue) === number.EIGHT) {
      dispatch(setExpanded(["comments"]));
      dispatch(getFocusedSection("comments"));
    }
  }, [expandValue]);

  useEffect(() => {
    return () => {
      if (provider) provider?.disconnect();
    };
  }, [provider]);

  /*
   * Parent Component: TaskSidebar
   * ShowTaskDetailPanel
   * @author Muskan
   */
  useEffect(() => {
    dispatch(setShowTaskDetailPanel(showSimplifiedDetail));
    dispatch(setShowApprovalLogs(showSimplifiedDetail));
  }, [showSimplifiedDetail]);

  useEffect(() => {
    let desc;
    if (isNewTask) {
      desc = newTaskData?.description ? newTaskData?.description : "";
    } else {
      desc = task?.Description ? task?.Description : "";
    }
    setTempDesc(showDescPopup?.status ? desc : null);
    dispatch(setTempTaskDesc(desc));
  }, [showDescPopup?.status]);

  useEffect(() => {
    dispatch(setShowEmailDescPopup(false));
  }, []);

  /**
   * Sets expanded state of PanelBar on initial render
   * @author Himanshu Negi
   */
  useEffect(() => {
    const expandedItems = getOrderedNavbarOptionsForTaskSidebar()?.filter((option) => option.expandCondition());
    let result = expandedItems.map((ele) => ele.id);
    dispatch(setExpanded(_.uniq([...result])));
  }, [
    task?.parentLinkTaskCount,
    newTaskData?.parentLinkTaskCount,
    newTaskData?.attachmentCount,
    newTaskData?.childOrSubtaskCount,
    currentFocus,
    task?.ShowLockedDescription,
    task?.InApproval?.data?.[number.ZERO],
    task?.IsApproved?.data?.[number.ZERO],
    task?.taskId,
  ]);

  const handleSelect = (event) => {
    if (event.target.props.id === "task-details") {
      dispatch(setShowTaskDetailPanel(!showTaskDetailPanel));
      return;
    }
    if (event.target.props.id === "parent-task-details") dispatch(setShowParentTaskDescription(!showDescription));
    if (event.target.props.id === "task-approval") dispatch(setShowApprovalLogs(!showApprovalLogs));
    if (event.target.props.id === "task-workflow") return dispatch(setExpanded(event.expandedItems));
    if (event.target.props.id === "task-triggered-tasks") {
      const isExpanding = !expanded.includes("task-triggered-tasks");
      if (isExpanding) {
        dispatch(setShowTriggerTaskPopup({ status: true, type: "triggered-tasks" }));
      } else {
        dispatch(setShowTriggerTaskPopup({ status: false, type: "" }));
      }
      if (event.expandedItems) {
        dispatch(setExpanded(event.expandedItems));
      }
      return;
    }
    if (event.expandedItems) {
      handleDescSection(event.expandedItems);
      dispatch(setExpanded(event.expandedItems));
    }
  };

  const resetScroller = () => {
    setOffset(0);
    setCurrentEmailData(true);
  };

  /**
   * Returns component on the basis of expanded state item
   * @param {String} id
   * @returns {JSX.Element}
   * @author Himanshu Negi
   */
  const getComponent = (id) => {
    switch (id) {
      case "task-details":
        return <TaskDetail />;
      case "task-description":
        return <TaskDescription provider={provider} setProvider={setProvider} />;
      case "parent-task-details":
        return <ParentTaskDetailPanel />;
      case "task-email":
        return (
          <TaskEmail
            setOffset={setOffset}
            offset={offset}
            setCurrentEmailData={setCurrentEmailData}
            currentEmailData={currentEmailData}
            resetScroller={resetScroller}
          />
        );
      case "task-attachments":
        return <Attachments />;
      case "task-comments":
        return <TaskComments />;
      case "task-workflow":
        return <WorkflowElementsPanel />;
      case "task-view-control":
        return <ViewControlPanel />;
      case "task-activity-log":
        return <ActivityLog />;
      case "task-linked-tasks":
        return <LinkedSubTasks />;
      case "task-approval":
        return <Approvals />;
      case "task-approval-description":
        return <ApprovalDescription />;
      case "task-triggered-tasks":
        return <></>;
    }
  };
  return (
    <div>
      {getOrderedNavbarOptionsForTaskSidebar()
        .map((option) => {
          const show = option.renderCondition(option.render);
          if (show) {
            const label = option.label instanceof Function ? option.label() : option.label;
            return (
              <PanelBar
                key={option.id}
                isControlled={true}
                expanded={expanded}
                onSelect={handleSelect}
                className={
                  label == "Details"
                    ? showTaskDetailPanel
                      ? "simplfified-task-detail-panel"
                      : ""
                    : label == "Approval"
                    ? showApprovalLogs
                      ? "simplfified-approval-panel"
                      : ""
                    : !showDescription
                    ? "simplfified-parent-task-panel"
                    : ""
                }
                expandMode={label == "Details" || label == "Parent Task Details" || label == "Approval" ? "multiple" : "multiple"}>
                <PanelBarItem
                  key={option.id}
                  id={option.id}
                  title={
                    <CustomPanelBarItemContent
                      title={label}
                      showTaskDetailPanel={showTaskDetailPanel}
                      flowElementsSectionRef={option.id === FLOW_ELEMENTS_SECTION_ID ? flowElementsSectionRef : null}
                    />
                  }
                  className={`${label === "Details" || label === "Approval" ? "task-detail-panel" : ""}`}>
                  {defaultDetails?.isAdded && show && getComponent(option.id)}
                </PanelBarItem>
              </PanelBar>
            );
          }
        })
        .filter(Boolean)}
      {showEmailDescPopup && <EmailDescription setOffset={setOffset} resetScroller={resetScroller} />}
      {showDescPopup.status && showDescriptionFromTaskSidebar && <DescriptionPopup provider={provider} setProvider={setProvider} />}
      {showFormPopup && (
        <FormProvider>
          <TaskForm />
        </FormProvider>
      )}
      {showTriggerTaskPopup.status && showTriggerTaskPopup.type === "triggered-tasks" && (
        <TriggeredTasksPopup
          key={`triggered-tasks-${task?.taskId || "default"}`}
          isOpen={showTriggerTaskPopup.status}
          onClose={() => dispatch(setShowTriggerTaskPopup({ status: false, type: "" }))}
          parentTaskId={task?.taskId}
        />
      )}
    </div>
  );
};
export default TaskSidebarContent;
